/**
 * LegalDocumentPage
 *
 * Shared shell for the six legal leaf pages (Terms of Use, Rewards Policy,
 * Exclusive Subscriptions Policy, Privacy Policy, Coins Policy, Premium
 * Subscriptions Policy). Each leaf page is a one-line wrapper that supplies
 * its title and its imported HTML content.
 *
 * Provides the page lifecycle (`useBasePage`), the shared `ExpauseAppBar`
 * (back button + centered title), and the scrollable body that renders the
 * policy HTML with theme-aware text + link colors.
 *
 * App-bar provenance: the six `lib/presentation/web/*_policy_screen.dart` /
 * `terms_of_use_screen.dart` shells this page ports are `HtmlElementView`
 * wrappers with **no** `Scaffold.appBar`, so the bar is a pre-existing
 * web-only addition (`workflow/lessons.md` — a full-page web route needs a
 * back affordance or a deep-linked user is stranded), shaped after the
 * *mobile* WebView screen `lib/presentation/screens/legal/privacy_policy_screen.dart:140-151`
 * (centered title + leading control) and Flutter's shared
 * `lib/presentation/custom_widgets/base_app_bar.dart:5-42` (whose default
 * back-chevron leading the web keeps, rather than that screen's close icon).
 * This is not a Flutter-parity claim for the bar itself.
 *
 * Moving from the hand-rolled bar to `ExpauseAppBar` makes three intentional
 * visual changes: the title steps from `responsive.font.lg`/`semibold` to
 * `responsive.font.xl`/`bold`, the bar becomes sticky with a blurred
 * 50%-opacity backdrop, and the top safe-area inset is now honoured.
 *
 * Accessible without authentication; mounted under PUBLIC_ROUTES.
 */
import { useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { Navigator } from '@/presentation/routing/Navigator';
import { ExpauseAppBar } from '@/presentation/components/common/appBar/ExpauseAppBar';
import { useBasePage } from '@/presentation/hooks/useBasePage';
import { useKeepAlive } from '@/presentation/routing/RouteStack';
import { generateScreenKey } from '@/presentation/routing/screenKeyUtils';
import { qaAttr } from '@/presentation/utils/qaAttr';

interface LegalDocumentPageProps {
  title: string;
  htmlContent: string;
  /**
   * `data-qa-id` for the page root, so the qa-tester can name each legal leaf
   * page distinctly (e.g. `legal-terms`). Defaults to `'legal-document'` for
   * the shared shell.
   */
  qaId?: string;
}

export const LegalDocumentPage = ({ title, htmlContent, qaId = 'legal-document' }: LegalDocumentPageProps) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const location = useLocation();

  // Canonical screen-key wiring lives in the shared shell so all six leaf
  // wrappers (TermsOfUsePage, CoinsPolicyPage, …) inherit it automatically.
  // Without a screen key the `KeepAliveOutlet` cannot disambiguate two
  // pushed instances of the same legal page, and `useBasePage` lifecycle
  // events would not pair with the right cache entry.
  const { currentScreenKeyRef } = useKeepAlive();
  const screenKeyRef = useRef<string>(
    currentScreenKeyRef.current || generateScreenKey(location.pathname)
  );
  const screenKey = screenKeyRef.current;

  useBasePage({ screenKey, onInit: () => { } });

  // Deep-link guard: with no in-app history, fall back to the
  // legal-documents list page so the user lands somewhere sensible
  // inside the app.
  const handleBack = () => Navigator.goBackOr(() => Navigator.navigateToLegalDocuments());

  return (
    <div
      {...qaAttr({ id: qaId, status: 'ready' })}
      style={{
        minHeight: '100vh',
        backgroundColor: theme.background,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* App Bar */}
      <ExpauseAppBar
        rootQa={{ id: 'legal-app-bar' }}
        title={title}
        leadingQa={{ id: 'legal-back' }}
        onLeadingTap={handleBack}
      />

      {/* Content */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: `${responsive.spacing.base}px`,
          color: theme.text.primary,
          fontSize: responsive.font.base,
          lineHeight: 1.6,
          '--link-color': theme.blue,
        } as React.CSSProperties}
        dangerouslySetInnerHTML={{ __html: htmlContent }}
      />
    </div>
  );
};
